import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FooterComponent } from '../../shared/components/footer/footer.component';

export interface AboutUsProblemSolutionRow {
  readonly problemTitle: string;
  readonly problemBody: string;
  readonly solutionTitle: string;
  readonly solutionBody: string;
}

export interface AboutUsTechPill {
  readonly label: string;
  readonly description: string;
}

export interface AboutUsCapabilityCard {
  readonly iconSrc: string;
  readonly title: string;
  readonly body: string;
}

export interface AboutUsFeatureItem {
  readonly iconSrc: string;
  readonly title: string;
  readonly body: string;
  readonly fullWidth?: boolean;
}

export interface AboutUsBenefitCard {
  readonly iconSrc: string;
  readonly title: string;
  readonly body: string;
  readonly tags: readonly string[];
}

@Component({
  selector: 'app-about-us',
  standalone: true,
  imports: [RouterModule, FooterComponent],
  templateUrl: './about-us.component.html',
  styleUrl: './about-us.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AboutUsComponent {
  readonly heroBadgeIconSrc = '/icons/shield.svg';

  readonly whyBadgeIconSrc = '/icons/doctor.svg';

  readonly coreBadgeIconSrc = '/icons/shield-2.svg';

  readonly featuresBadgeIconSrc = '/icons/box-2.svg';

  readonly arrowIconSrc = '/icons/right-arrow-blue.svg';

  readonly benefitsBadgeIconSrc = '/icons/people.svg';

  readonly problemSolutionRows: AboutUsProblemSolutionRow[] = [
    {
      problemTitle: 'Siloed data',
      problemBody:
        'Data locked within institutions limits collaboration, innovation and longitudinal healthcare insights.',
      solutionTitle: 'Federated, modular architecture',
      solutionBody:
        'Connects distributed ecosystems while allowing organizations to retain control and customize healthcare use cases.',
    },
    {
      problemTitle: 'Access barriers',
      problemBody:
        'Complex permissions and privacy concerns restrict timely and compliant data availability.',
      solutionTitle: 'Secure and private data exchange',
      solutionBody:
        'Consent-driven and role-based access supports safe, compliant and trusted data sharing across institutions.',
    },
  ];

  readonly techPills: AboutUsTechPill[] = [
    { label: 'FHIR', description: 'Interoperable data standards' },
    { label: 'RBAC', description: 'Governance and privacy controls' },
    { label: 'AI', description: 'Actionable intelligence workflows' },
  ];

  readonly coreCapabilities: AboutUsCapabilityCard[] = [
    {
      iconSrc: '/icons/dataset.svg',
      title: 'Unified Data Catalogue',
      body: 'Discover and assess healthcare datasets with rich metadata, standardized descriptions and a structured discovery experience.',
    },
    {
      iconSrc: '/icons/model-blue.svg',
      title: 'Secure Data Exchange APIs',
      body: 'Open-source API-based access for real-time and historical healthcare data with secure authentication and policy enforcement.',
    },
    {
      iconSrc: '/icons/check-cloud.svg',
      title: 'Consent-driven Data Access',
      body: 'Purposeful, auditable sharing with privacy protection, consent validation and compliant access for regulated healthcare contexts.',
    },
    {
      iconSrc: '/icons/key.svg',
      title: 'Data Provider Control',
      body: 'Providers retain sovereignty over data access and usage while enabling controlled collaboration across the ecosystem.',
    },
    {
      iconSrc: '/icons/bottle.svg',
      title: 'Analytics Sandbox',
      body: 'Hosted notebooks, profiling tools, model experimentation and workflow exploration for healthcare-specific analytics applications.',
    },
    {
      iconSrc: '/icons/people.svg',
      title: 'Community Layer',
      body: 'Discussions, challenges, learning experiences and collaborative innovation tools for providers, researchers and developers.',
    },
  ];

  readonly keyFeatures: AboutUsFeatureItem[] = [
    {
      iconSrc: '/icons/dataset.svg',
      title: 'Datasets',
      body: 'Browse healthcare datasets with rich metadata, discovery tools and governed access for research, policy and innovation workflows.',
    },
    {
      iconSrc: '/icons/wire.svg',
      title: 'Models',
      body: 'Access reusable analytical and AI models tailored for healthcare use cases, benchmarking and interoperable deployment pipelines.',
    },
    {
      iconSrc: '/icons/doctor.svg',
      title: 'Usecases',
      body: 'Discover practical healthcare-specific analytics applications and implementation scenarios across hospitals, public health and research.',
    },
    {
      iconSrc: '/icons/laptop.svg',
      title: 'Sandbox IDE',
      body: 'Experiment with datasets, notebooks and model workflows in a controlled environment built for healthcare analytics teams.',
    },
    {
      iconSrc: '/icons/trophy.svg',
      title: 'Leaderboard',
      body: 'Track leading organisations, asset activity and platform engagement through a clear and competitive ranking experience.',
    },
    {
      iconSrc: '/icons/flag.svg',
      title: 'Challenges',
      body: 'Participate in innovation challenges that encourage healthcare solution development using governed data and models.',
    },
  ];

  readonly benefitCards: AboutUsBenefitCard[] = [
    {
      iconSrc: '/icons/building.svg',
      title: 'Hospitals & Providers',
      body:
        'Publish, govern and share healthcare data responsibly while retaining control over access, privacy and operational policies.',
      tags: ['Secure onboarding', 'Provider control'],
    },
    {
      iconSrc: '/icons/research.svg',
      title: 'Researchers',
      body:
        'Access governed, standardized and interoperable datasets to accelerate evidence generation, benchmarking and collaborative studies.',
      tags: ['Trusted access', 'Standardized data'],
    },
    {
      iconSrc: '/icons/building-2.svg',
      title: 'Government & Public Health',
      body:
        'Build a unified view for planning, surveillance, policy and population-scale decision-making using governed exchange workflows.',
      tags: ['Policy insights', 'Population analytics'],
    },
    {
      iconSrc: '/icons/rocket.svg',
      title: 'Startups & Innovators',
      body:
        'Leverage datasets, sandboxes, models and challenge ecosystems to build new healthcare products and digital services faster.',
      tags: ['Sandbox tools', 'AI-ready services'],
    },
  ];
}
